// src/components/users/ReturnItemFromOfficerModal.tsx
// ✅ FIXED 2024-12-22: Dynamic storage locations from tenant config

import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Package, RefreshCw } from 'lucide-react';
import { Assignment, ReturnRequest, RETURN_CONDITIONS } from '../../types/assignment';
import { assignmentAPI } from '../../services/assignmentAPI';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';
import { logger } from '../../utils/logger';

const returnModalLogger = logger.createServiceLogger('ReturnItemModal');

interface ReturnItemFromOfficerModalProps {
  isOpen: boolean;
  assignment: Assignment | null;
  onClose: () => void;
  onReturnComplete: () => void;
}

const ReturnItemFromOfficerModal: React.FC<ReturnItemFromOfficerModalProps> = ({
  isOpen,
  assignment,
  onClose,
  onReturnComplete
}) => {
  const [returnData, setReturnData] = useState<ReturnRequest>({
    condition: 'Good',
    returnToLocation: '',
    returnedBy: '',
    notes: '',
    quantity: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadLocations();
      setReturnData(prev => ({
        ...prev,
        returnedBy: getCurrentUser()
      }));
    }
  }, [isOpen]);

  useEffect(() => {
    if (assignment) {
      setReturnData(prev => ({
        ...prev,
        condition: 'Good',
        returnToLocation: '',
        notes: '',
        quantity: 1
      }));
      setError(null);
    }
  }, [assignment]);

  // ✅ FIXED: Load locations dynamically from tenant configuration
  const loadLocations = async () => {
    setLocationsLoading(true);
    try {
      returnModalLogger.debug('Loading storage locations from tenant config');
      
      const dynamicLocations = await tenantConfigAPI.getInventoryLocations();
      
      if (dynamicLocations && dynamicLocations.length > 0) {
        returnModalLogger.info('Loaded dynamic storage locations', { 
          count: dynamicLocations.length,
          locations: dynamicLocations 
        });
        setLocations(dynamicLocations);
      } else {
        // Fallback only if no locations configured
        returnModalLogger.warn('No storage locations configured, using fallback');
        const fallbackLocations = [
          'Main Storage',
          'Evidence Locker',
          'Armory'
        ];
        setLocations(fallbackLocations);
      }
    } catch (err) {
      returnModalLogger.error('Error loading locations', undefined, err as Error);
      // Use minimal fallback on error
      setLocations(['Main Storage']);
    } finally {
      setLocationsLoading(false);
    }
  };

  const getCurrentUser = () => {
    // Try to get actual user name from localStorage or auth context
    const firstName = localStorage.getItem('userFirstName') || '';
    const lastName = localStorage.getItem('userLastName') || '';
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return 'Current User';
  };

  const isSerializedItem = () => {
    return assignment?.itemSerialNumber !== undefined && assignment?.itemSerialNumber !== '';
  };

  const isBulkItem = () => {
    return !isSerializedItem();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!assignment) return;

    if (!returnData.returnToLocation) {
      setError('Return location is required');
      return;
    }

    if (isBulkItem() && (!returnData.quantity || returnData.quantity < 1)) {
      setError('Quantity must be at least 1');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      returnModalLogger.info('Processing equipment return', {
        assignmentId: assignment.assignmentId,
        itemName: assignment.itemName,
        condition: returnData.condition,
        returnToLocation: returnData.returnToLocation
      });
      
      const response = await assignmentAPI.returnAssignment(assignment.assignmentId, {
        condition: returnData.condition as 'Good' | 'Damaged' | 'Lost',
        notes: returnData.notes
      });
      
      if (response.success) {
        returnModalLogger.info('Return processed successfully', {
          assignmentId: assignment.assignmentId
        });
        onReturnComplete();
        onClose();
      } else {
        throw new Error(response.error?.message || 'Failed to return item');
      }
    } catch (err) {
      returnModalLogger.error('Error returning item', undefined, err as Error);
      setError(err instanceof Error ? err.message : 'Failed to return item');
    } finally {
      setLoading(false);
    }
  };

  const getConditionDescription = (condition: string) => {
    switch (condition) {
      case 'Good':
        return 'Item is in good condition and ready for reissue';
      case 'Damaged':
        return 'Item needs repair or maintenance before reissue';
      case 'Lost':
        return 'Item is lost/destroyed and should be removed from inventory';
      default:
        return '';
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'Good':
        return 'text-green-600';
      case 'Damaged':
        return 'text-yellow-600';
      case 'Lost':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  if (!isOpen || !assignment) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Return Equipment</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 max-h-[calc(90vh-80px)] overflow-y-auto">
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <Package className="w-8 h-8 text-blue-600" />
              <div>
                <h3 className="font-medium text-gray-900">{assignment.itemName}</h3>
                {assignment.itemSerialNumber && (
                  <p className="text-sm text-gray-600">Serial: {assignment.itemSerialNumber}</p>
                )}
                <p className="text-sm text-gray-600">
                  Officer: {assignment.assignedToUserName}
                </p>
                <p className="text-sm text-gray-600">
                  Assigned: {new Date(assignment.assignmentDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSerializedItem() && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition on Return
                </label>
                <div className="space-y-3">
                  {Object.values(RETURN_CONDITIONS).map((condition) => (
                    <label key={condition} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        value={condition}
                        checked={returnData.condition === condition}
                        onChange={(e) => setReturnData(prev => ({ ...prev, condition: e.target.value }))}
                        className="mt-1"
                      />
                      <div>
                        <div className={`font-medium ${getConditionColor(condition)}`}>
                          {condition} - {condition === 'Good' ? 'Reissue Ready' : 
                                       condition === 'Damaged' ? 'Needs Service' : 
                                       'Remove from Inventory'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {getConditionDescription(condition)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {isBulkItem() && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity Returned
                </label>
                <input
                  type="number"
                  min="1"
                  value={returnData.quantity}
                  onChange={(e) => setReturnData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Return all or partial quantity of assigned items
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return to Location <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={returnData.returnToLocation}
                  onChange={(e) => setReturnData(prev => ({ ...prev, returnToLocation: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  required
                  disabled={locationsLoading}
                >
                  <option value="">
                    {locationsLoading ? 'Loading locations...' : 'Select location...'}
                  </option>
                  {locations.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
                {locationsLoading && (
                  <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                    <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
              {locations.length === 0 && !locationsLoading && (
                <p className="text-xs text-amber-600 mt-1">
                  No storage locations configured. Please contact your administrator.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Returned By
              </label>
              <input
                type="text"
                value={returnData.returnedBy}
                onChange={(e) => setReturnData(prev => ({ ...prev, returnedBy: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                readOnly
              />
              <p className="text-xs text-gray-500 mt-1">Current user (automatically filled)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Return Notes
              </label>
              <textarea
                value={returnData.notes}
                onChange={(e) => setReturnData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about the return..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {error && (
              <div className="flex items-center space-x-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {isSerializedItem() && returnData.condition === 'Lost' && (
              <div className="flex items-start space-x-2 text-orange-600 bg-orange-50 border border-orange-200 rounded-lg p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <strong>Warning:</strong> This item will be marked as lost/destroyed and removed from available inventory permanently.
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !returnData.returnToLocation || locationsLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Return Item</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReturnItemFromOfficerModal;